import React, { useEffect, useState } from 'react';
import useTimer from '../../hooks/useTimer';
import useTodos from '../../hooks/useTodos';
import { feedbackService } from '../../services/feedbackService';
import FlipNumbers from 'react-flip-numbers';
import './timer.css';

export default function TimerPanel() {
  const { state, start, pause, reset, skip, setSettings, setAttachedTodo, adjustRemaining } = useTimer();
  const { mode, remaining, running, round, settings, lastEvent, attachedTodo } = state;
  const { todos } = useTodos();
  const [zen, setZen] = useState(false);

  const mm = Math.floor(remaining / 60).toString().padStart(2, '0');
  const ss = (remaining % 60).toString().padStart(2, '0');

  // Keyboard shortcuts
  useEffect(() => {
    const onKey = (e) => {
      if (['INPUT','TEXTAREA','SELECT'].includes(e.target.tagName)) return;
      if (e.code === 'Space') { e.preventDefault(); running ? pause() : start(); }
      if (e.key.toLowerCase() === 'r') reset();
      if (e.key.toLowerCase() === 's') skip();
      if (e.key.toLowerCase() === 'f') toggleFullscreen();
      if (e.key === '+') adjustMinutes(1);
      if (e.key === '-') adjustMinutes(-1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [running, start, pause, reset, skip]);

  // Confetti on focus complete
  useEffect(() => {
    if (lastEvent?.type === 'focus_complete') {
      feedbackService.fireConfetti();
    }
  }, [lastEvent]);

  function toggleZen() {
    setZen((z) => {
      const next = !z;
      document.body.classList.toggle('zen', next);
      return next;
    });
  }

  function toggleFullscreen() {
    try {
      if (!document.fullscreenElement) {
        document.documentElement.requestFullscreen?.();
      } else {
        document.exitFullscreen?.();
      }
    } catch {}
  }

  function adjustMinutes(delta) {
    adjustRemaining(delta);
  }

  // Settings form handlers
  function handleChange(e) {
    const { name, value } = e.target;
    let v = parseInt(value, 10);
    if (isNaN(v) || v < 1) v = 1;
    setSettings({ ...settings, [name]: v });
  }

  return (
    <div className="timer">
      <div className="timer-mode" aria-live="polite">{mode === 'focus' ? 'Focus' : mode === 'short' ? 'Short Break' : 'Long Break'}</div>
      <div className="flip-clock" aria-label="Remaining time" role="timer">
        <FlipNumbers
          height={72}
          width={56}
          play
          perspective={600}
          numbers={mm[0]}
          numberStyle={{ fontSize: 40, fontWeight: 700, color: 'var(--text)', background: '#0b1220', borderRadius: 10, border: '1px solid #1f2937' }}
          duration={0.55}
        />
        <FlipNumbers
          height={72}
          width={56}
          play
          perspective={600}
          numbers={mm[1]}
          numberStyle={{ fontSize: 40, fontWeight: 700, color: 'var(--text)', background: '#0b1220', borderRadius: 10, border: '1px solid #1f2937' }}
          duration={0.55}
        />
        <div className="flip-colon">:</div>
        <FlipNumbers
          height={72}
          width={56}
          play
          perspective={600}
          numbers={ss[0]}
          numberStyle={{ fontSize: 40, fontWeight: 700, color: 'var(--text)', background: '#0b1220', borderRadius: 10, border: '1px solid #1f2937' }}
          duration={0.55}
        />
        <FlipNumbers
          height={72}
          width={56}
          play
          perspective={600}
          numbers={ss[1]}
          numberStyle={{ fontSize: 40, fontWeight: 700, color: 'var(--text)', background: '#0b1220', borderRadius: 10, border: '1px solid #1f2937' }}
          duration={0.55}
        />
      </div>
      {/* Attach active todo */}
      <div className="attached" style={{ marginTop: 8 }}>
        <label htmlFor="attach-select">Attach</label>{' '}
        <select
          id="attach-select"
          value={attachedTodo?.id || ''}
          onChange={(e) => {
            const id = e.target.value || null;
            const t = todos.find((x) => x.id === id);
            setAttachedTodo(id && t ? { id, title: t.title } : null);
          }}
        >
          <option value="">None</option>
          {todos.filter(t => t.status === 'open').map((t) => (
            <option key={t.id} value={t.id}>{t.title}</option>
          ))}
        </select>
        {attachedTodo?.title && <span style={{ marginLeft: 8, color: 'var(--muted)' }}>{attachedTodo.title}</span>}
      </div>
      <div className="controls">
        {!running ? (
          <button className="primary" onClick={start} aria-label="Start">Start</button>
        ) : (
          <button className="primary" onClick={pause} aria-label="Pause">Pause</button>
        )}
        <button className="ghost" onClick={reset} aria-label="Reset">Reset</button>
        {mode !== 'focus' && <button className="ghost" onClick={skip} aria-label="Skip Break">Skip</button>}
        <button className="ghost" onClick={toggleZen} aria-label="Zen mode">{zen ? 'Exit Zen' : 'Zen'}</button>
        <button className="ghost" onClick={toggleFullscreen} aria-label="Fullscreen">Fullscreen</button>
      </div>
      <div className="meta">
        <span>Round {round} / {settings.longEvery}</span>
      </div>
      {/* Settings UI */}
      <form className="timer-settings" aria-label="Timer Settings" style={{ marginTop: 16, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'center' }}>
        <label>
          Focus
          <input
            type="number"
            name="focus"
            min="1"
            max="90"
            value={settings.focus}
            onChange={handleChange}
          className="input compact"
            style={{ width: 48, marginLeft: 4 }}
          />
          min
        </label>
        <label title="Short break after each focus round" aria-label="Short break: a quick rest after each focus session">
          Short
          <span style={{ cursor: 'help', marginLeft: 2 }} aria-label="Short break: a quick rest after each focus session">🛈</span>
          <input
            type="number"
            name="shortBreak"
            min="1"
            max="30"
            value={settings.shortBreak}
            onChange={handleChange}
          className="input compact"
            style={{ width: 48, marginLeft: 4 }}
          />
          min
        </label>
        <label title="Long break after completing all rounds" aria-label="Long break: a longer rest after all focus rounds">
          Long
          <span style={{ cursor: 'help', marginLeft: 2 }} aria-label="Long break: a longer rest after all focus rounds">🛈</span>
          <input
          className="input compact"
            type="number"
            name="longBreak"
            min="1"
            max="60"
            value={settings.longBreak}
            onChange={handleChange}
            style={{ width: 48, marginLeft: 4 }}
          />
          min
        </label>
        <label title="Number of focus rounds before a long break" aria-label="Rounds: number of focus sessions before a long break">
          Rounds
          <input
            type="number"
            name="longEvery"
            min="1"
            max="10"
            value={settings.longEvery}
            onChange={handleChange}
          className="input compact"
            style={{ width: 48, marginLeft: 4 }}
          />
        </label>
      </form>
    </div>
  );
}

// FlipUnit removed; replaced by react-flip-numbers
